import { FC, useState, useCallback, useRef } from 'react'

import { utils, constants, BigNumber } from 'ethers'

import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import OutlinedInput from '@mui/material/OutlinedInput'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import CheckIcon from '@mui/icons-material/Check'

import useDApp from 'contexts/Web3'
import { useNavKnights } from 'routes'

import UglyDialog from 'components/UglyDialog'
import UglySnackbar from 'components/UglySnackbar'

import useContractInteraction from 'hooks/useContractInteraction'
import isSameAddress from 'utils/isSameAddress'
import useTokenId from './useTokenId'

const QueenTime = 7 * 24 * 60 * 60 * 1000

interface SeatProps {
  // 除了 close 以外全部都 readonly
  disabled?: boolean

  // 使用者狀態:
  pmtEnabled?: boolean
  ttBalance: number
  pmtBalance: number

  // 位子基本設定
  pmtRequired: number
  ttJoinRequired: number
  ttReplaceFee: number // 0.05 = 5%

  // 位子當前狀態:
  ttStaked: number
  ttProfit: number
  isQueenSeat: boolean
  canHarvest: boolean

  // Actions
  onApprovePMT: () => void
  onJoinSeat: (staking: BigNumber, payment: BigNumber) => void
  onLeaveSeat: () => void
  onHarvest: () => void
  onClose: () => void
}

const Input = styled(OutlinedInput)({
  background: '#FFF',

  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: 4,
    borderColor: '#683100',
  },

  '&.Mui-disabled': {
    background: `rgba(255, 255, 255, .6)`,
  },
})

const Adornment = styled(InputAdornment)({
  '& .MuiTypography-root': { color: '#683100' },

  '.Mui-disabled &': { opacity: .3 },
})

const InnerList = styled('ol')({
  paddingLeft: '1em',

  '& > li': { marginBottom: '1em' },
})

const useAmountInput = (defaultValue: number = 0) => {
  const inputRef = useRef()
  const [toStakeAmount, updateStakeAmount] = useState(defaultValue.toFixed(2))
  const stakingAmount = Number(toStakeAmount)

  const onAmountChange = useCallback(() => {
    const value = ((inputRef.current as any)?.value ?? '' as string).trim()

    if (!value) return updateStakeAmount('0')
    if (!Number.isNaN(Number(value))) return updateStakeAmount(value)
  }, [])

  return {
    inputRef,
    onAmountChange,
    stakingAmount,
  }
}

const EmptySeat: FC<SeatProps> = (props) => {
  const { onJoinSeat } = props
  const { inputRef, onAmountChange, stakingAmount } = useAmountInput(props.ttJoinRequired)

  const onJoin = useCallback(() => {
    const amount = utils.parseEther(stakingAmount.toFixed(5))
    return onJoinSeat(amount, amount)
  }, [onJoinSeat, stakingAmount])

  const pmtShortage = props.pmtBalance < props.pmtRequired
  const ttShortage = props.ttBalance < stakingAmount

  let buttonText = 'Take the seat'
  let buttonDisabled = props.disabled
    || !props.pmtEnabled
    || stakingAmount < props.ttJoinRequired

  if (props.pmtEnabled && pmtShortage) {
    buttonText = 'Out of PMT'
    buttonDisabled = true
  }

  if (ttShortage) {
    buttonText = 'Out of TT'
    buttonDisabled = true
  }

  return (
    <UglyDialog title="Take the seat" open onClose={props.onClose}>
      <Typography component="h3" variant="UglyDialogInnerSubject">
        Details
      </Typography>
      <InnerList>
        <Typography component="li" variant="UglyDialogInnerText">
          Entrance: Burn { props.pmtRequired } PMT &nbsp;
          <Button
            variant="ugly"
            color={ props.pmtEnabled ? 'warning' : 'success' }
            disabled={props.pmtEnabled || props.disabled}
            startIcon={props.pmtEnabled ? <CheckIcon /> : null}
            onClick={props.onApprovePMT}
          >
            Enable PMT
          </Button>
        </Typography>
        <Typography component="li" variant="UglyDialogInnerText">
          Stake TT for the seat
          <Typography component="p" variant="UglyDialogInnerHint">
            Minimum amount: {props.ttJoinRequired} TT
          </Typography>
          <Box height={8} />
          <Input
            inputRef={inputRef}
            size="small"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            color="secondary"
            fullWidth
            endAdornment={<Adornment position="end">TT</Adornment>}
            defaultValue={props.ttJoinRequired.toFixed(2)}
            readOnly={props.disabled}
            onChange={onAmountChange}
          />
        </Typography>
      </InnerList>
      <Box mt={4} mb={1} textAlign="center">
        <Button
          variant="ugly"
          color="success"
          disabled={buttonDisabled}
          onClick={onJoin}
        >
          { buttonText }
        </Button>
      </Box>
    </UglyDialog>
  )
}

/** 使用者自己的座位 */
const OwnedSeat: FC<SeatProps> = (props) => {
  return (
    <UglyDialog title="Your Seat" open onClose={props.onClose}>
      <Typography component="h3" variant="UglyDialogInnerSubject">
        Current stats
      </Typography>

      <Stack direction="row" justifyContent="space-between">
        <Typography variant="UglyDialogInnerText">
          Stake
        </Typography>
        <Typography variant="UglyDialogInnerText">
          { props.ttStaked.toFixed(3) } TT
        </Typography>
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Typography variant="UglyDialogInnerText">
          Profit
        </Typography>
        <Typography variant="UglyDialogInnerText">
          { props.ttProfit.toFixed(3) } TT
        </Typography>
      </Stack>

      <Box height={20} />

      <Typography component="h3" variant="UglyDialogInnerSubject">
        Leave stats
      </Typography>

      <Stack direction="row" justifyContent="space-between">
        <Typography variant="UglyDialogInnerText">
          Leave fee
        </Typography>
        <Typography variant="UglyDialogInnerText">
          { (props.ttStaked > 0 ? props.ttStaked * .05 : 0).toFixed(3) } TT
        </Typography>
      </Stack>
      <Typography variant="UglyDialogInnerHint">5% of staking</Typography>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="UglyDialogInnerText">
          Settlement
        </Typography>
        <Typography variant="UglyDialogInnerText">
          { (props.ttProfit + (props.ttStaked > 0 ? props.ttStaked * .95 : 0)).toFixed(3) } TT
        </Typography>
      </Stack>

      <Box textAlign="center" mt={2} mb={3}>
        <Button
          variant="ugly"
          color="success"
          onClick={props.onLeaveSeat}
          disabled={props.disabled || props.canHarvest}
        >
          Leave the seat
        </Button>
      </Box>

      <Typography component="h3" variant="UglyDialogInnerSubject">
        Harvest
      </Typography>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="UglyDialogInnerText">
          Settlement
        </Typography>
        <Typography variant="UglyDialogInnerText">
          { (props.ttProfit > 0 ? props.ttProfit : 0).toFixed(3) } TT
        </Typography>
      </Stack>

      <Box textAlign="center" mt={2}>
        <Button
          variant="ugly"
          color="success"
          onClick={props.onHarvest}
          disabled={props.disabled || !props.canHarvest}
        >
          Harvest
        </Button>
      </Box>
      <Box mt={1}>
        <Typography variant="UglyDialogInnerHint">Being the most powerful knight and keep your seat for 7 days to harvest all you profits.</Typography>
      </Box>
    </UglyDialog>
  )
}

/** 別人持有的座位，可搶奪狀態 */
const TakenSeat: FC<SeatProps> = (props) => {
  const { onJoinSeat, ttReplaceFee, ttStaked } = props
  const { inputRef, onAmountChange, stakingAmount } = useAmountInput(props.ttJoinRequired)

  const onReplaceSeat = useCallback(() => {
    const replaceFee = utils.parseUnits(
      Math.ceil(1000 * ttStaked * ttReplaceFee).toString(),
      15
    )
    const stakeAmount = utils.parseEther(stakingAmount.toFixed(5))
    return onJoinSeat(stakeAmount, stakeAmount.add(replaceFee))
  }, [stakingAmount, onJoinSeat, ttReplaceFee, ttStaked])

  const pmtShortage = props.pmtBalance < props.pmtRequired
  const ttShortage = props.ttBalance < stakingAmount

  let buttonText = 'Replace the seat'
  let buttonDisabled = props.disabled
    || !props.pmtEnabled
    || stakingAmount < props.ttJoinRequired

  if (props.pmtEnabled && pmtShortage) {
    buttonText = 'Out of PMT'
    buttonDisabled = true
  }

  if (ttShortage) {
    buttonText = 'Out of TT'
    buttonDisabled = true
  }

  return (
    <UglyDialog title="Replace the seat" open onClose={props.onClose}>
      <Typography component="h3" variant="UglyDialogInnerSubject">
        Details
      </Typography>
      <InnerList>
        <Typography component="li" variant="UglyDialogInnerText">
          Burn { props.pmtRequired } PMT &nbsp;
          <Button
            variant="ugly"
            color={ props.pmtEnabled ? 'warning' : 'success' }
            disabled={ props.pmtEnabled || props.disabled }
            startIcon={props.pmtEnabled ? <CheckIcon /> : null}
            onClick={props.onApprovePMT}
          >
            Enable PMT
          </Button>
        </Typography>
        <Typography component="li" variant="UglyDialogInnerText">
          Replace fee: &nbsp; {(ttStaked * ttReplaceFee).toFixed(1)} TT
          <Typography component="p" variant="UglyDialogInnerHint">
            {(props.ttReplaceFee * 100).toFixed(0)}% of current staking
          </Typography>
        </Typography>
        <Typography component="li" variant="UglyDialogInnerText">
          Stake TT for the seat
          <Typography component="p" variant="UglyDialogInnerHint">
            Minimum amount: {props.ttJoinRequired} TT
          </Typography>
          <Box height={8} />
          <Input
            inputRef={inputRef}
            size="small"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            color="secondary"
            fullWidth
            endAdornment={<Adornment position="end">TT</Adornment>}
            defaultValue={props.ttJoinRequired.toFixed(2)}
            readOnly={props.disabled}
            onChange={onAmountChange}
          />
        </Typography>
      </InnerList>
      <Box mt={4} mb={1} textAlign="center">
        <Button
          variant="ugly"
          color="success"
          disabled={buttonDisabled}
          onClick={onReplaceSeat}
        >
          { buttonText }
        </Button>
      </Box>
    </UglyDialog>
  )
}

const Seat: FC = () => {
  const approveState = useContractInteraction()
  const joinState = useContractInteraction()
  const leaveState = useContractInteraction()
  const harvestState = useContractInteraction()

  const { actions, balances, configs, knight, wallet } = useDApp()
  const tokenId = useTokenId()
  const onClose = useNavKnights()
  const info = knight.list.find(({ id }) => id === tokenId)

  /// TT ---------------------------------------------

  const ttBalance = Number(balances.TT.display)

  /// PMT --------------------------------------------

  const pmtRequired = configs.Knight.EntranceFee
  const pmtBalance = Number(
    utils.formatEther(balances.PMT?.amount ?? constants.Zero)
  )
  const pmtAllowance = Number(
    utils.formatEther(knight.pmtAllowance ?? constants.Zero)
  )
  const pmtEnabled = pmtAllowance >= pmtRequired

  const now = Date.now()
  const timeStart = Number(info?.since) > 0 ? info!.since * 1000 : 0
  const durationTaken = timeStart > 0 && now > timeStart ? now - timeStart : 0

  const isOwner = info?.owner && isSameAddress(info.owner, wallet.account)
  const hasProfit = Number(info?.profit) > 0
  const isQueenSeat = knight.queenId === (info?.id ?? null)
  const canHarvest = isQueenSeat && hasProfit && durationTaken > QueenTime

  const disabled = !wallet.account
    || approveState.state !== 'idle'
    || joinState.state !== 'idle'
    || leaveState.state !== 'idle'

  const onApprovePMT = useCallback(async () => {
    approveState.start()

    await actions
      .approvePMT()
      .then((success: boolean) => {
        if (success) return approveState.onDone()
        return approveState.onError('Enable PMT failure, please try again.')
      })
      .catch(approveState.onError)
      .finally(actions.refreshGameInfo)
    }, [actions, approveState])

  const onJoinSeat = useCallback(async (staking: BigNumber, payment: BigNumber) => {
    joinState.start()

    await actions
      .joinKnights(tokenId, staking, payment)
      .then((_result: {
        tokenId: BigNumber
        joiner: string
        staked: BigNumber
        fee: BigNumber
      }) => {
        return joinState.onDone()
      })
      .catch(joinState.onError)
      .finally(actions.refreshGameInfo)
  }, [actions, joinState, tokenId])

  const onLeaveSeat = useCallback(async () => {
    leaveState.start()

    await actions
      .leaveKnights(tokenId)
      .then((_result: {
        tokenId: BigNumber
        leaver: string
        staked: BigNumber
        profit: BigNumber
        fee: BigNumber
      }) => {
        return leaveState.onDone()
      })
      .catch(leaveState.onError)
      .finally(actions.refreshGameInfo)
  }, [actions, leaveState, tokenId])

  const onHarvest = useCallback(async () => {
    harvestState.start()

    await actions
      .harvest()
      .then((_result: {
        queenId: BigNumber
        owner: string
        profit: BigNumber
        fee: BigNumber
      }) => {
        return harvestState.onDone()
      })
      .catch(harvestState.onError)
      .finally(actions.refreshGameInfo)
  }, [actions, harvestState])

  const sharedProps = {
    // 除了 close 以外全部都 readonly
    disabled,

    // 使用者狀態:
    pmtEnabled,
    ttBalance,
    pmtBalance,

    // 位子資訊:
    pmtRequired,
    ttReplaceFee: knight.seatFee,
    ttJoinRequired: Math.max(
      knight.minJoin,
      (info?.amount ?? 0) + 100  // 佔位 TT 低消 -> 至少之前金額多 100
    ),

    // 位子狀態:
    ttStaked: info?.amount ?? 0,
    ttProfit: info?.profit ?? 0,
    isQueenSeat,
    canHarvest,

    onApprovePMT,
    onJoinSeat,
    onLeaveSeat,
    onHarvest,
    onClose,
  }

  let dialog = null
  if (isOwner) dialog = <OwnedSeat {...sharedProps} />
  else if (info?.owner) dialog = <TakenSeat {...sharedProps} />
  else dialog = <EmptySeat {...sharedProps} />

  return (
    <>
      { dialog }

      <UglySnackbar
        open={approveState.state !== 'idle'}
        onClose={approveState.reset}
        state={approveState.state}
      >
        <>{ approveState.error ?? 'Enabling PMT...' }</>
      </UglySnackbar>

      <UglySnackbar
        open={joinState.state !== 'idle'}
        onClose={joinState.reset}
        state={joinState.state}
      >
        <>{ joinState.error ?? 'Taking the seat...' }</>
      </UglySnackbar>

      <UglySnackbar
        open={leaveState.state !== 'idle'}
        onClose={leaveState.reset}
        state={leaveState.state}
      >
        <>{ leaveState.error ?? 'Leaving the seat...' }</>
      </UglySnackbar>

      <UglySnackbar
        open={harvestState.state !== 'idle'}
        onClose={harvestState.reset}
        state={harvestState.state}
      >
        <>{ harvestState.error ?? 'Harvesting...' }</>
      </UglySnackbar>
    </>
  )
}

export default Seat
